import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';

const IdeasImpressions = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const images = [
    // Featured Gallery Images
    {
      id: 26,
      category: 'birdcage',
      url: '/living_room_gallery_corrected_table.png',
      title: 'Living Room Gallery with Corrected Coffee Table Display'
    },
    {
      id: 27,
      category: 'birdcage',
      url: '/luxury_wooden_chalet.png',
      title: 'Luxury Wooden Chalet with Original Elements'
    },
    {
      id: 28,
      category: 'birdcage',
      url: '/living_room_gallery_adjusted_table.png',
      title: 'Living Room Gallery with Adjusted Coffee Table'
    },
    {
      id: 31,
      category: 'birdcage',
      url: '/pink_table_artourius.png',
      title: 'Modern Living Space with Artourius Sculpture and Pink Table'
    },
    {
      id: 32,
      category: 'birdcage',
      url: '/inspiration_living_room.png',
      title: 'Artourius Collection in Contemporary Art Interior'
    },
    {
      id: 33,
      category: 'birdcage',
      url: '/ornament_swiss_chalet_entrance.jpg',
      title: 'Chicken Cage Lightings at Swiss Chalet Entrance'
    },
    {
      id: 34,
      category: 'birdcage',
      url: '/ornament_outdoor_pool.jpg',
      title: 'Chicken Cage Lightings by the Outdoor Pool'
    },
    {
      id: 35,
      category: 'birdcage',
      url: '/pool_area_impressions.png',
      title: 'Luxury Pool Area with Artourius Sculptures and Lightings'
    },
    
    // Chicken Cage Lightings
    {
      id: 1,
      category: 'birdcage',
      url: '/general/lattice_lantern_turquoise_luxury_entryway.webp',
      title: 'Chicken Cage Lighting in Luxury Entryway'
    },
    {
      id: 7,
      category: 'birdcage',
      url: '/general/bathroom_lighting_turquoise.webp',
      title: 'Chicken Cage Lighting in Modern Bathroom'
    },
    {
      id: 8,
      category: 'birdcage',
      url: '/general/bubbles_birdcage_green_dining_table.webp',
      title: 'Chicken Cage Lighting in Contemporary Dining'
    },
    {
      id: 12,
      category: 'birdcage',
      url: '/general/geo_birdcage_red_bedside.webp',
      title: 'Chicken Cage Lighting as Bedside Lamp'
    },
    {
      id: 29,
      category: 'birdcage',
      url: '/living_room_gallery_lanterns.png',
      title: 'Living Room Gallery with Lantern Lights'
    },
    {
      id: 30,
      category: 'birdcage',
      url: '/living_room_gallery_with_articles.png',
      title: 'Living Room Gallery with Artourius Articles'
    },
    
    // Resin Tables
    {
      id: 17,
      category: 'resin',
      url: '/general/dubai_rooftop_shisha_lounge.webp',
      title: 'Exclusive Resin Coffee Table on Dubai Rooftop'
    },
    {
      id: 18,
      category: 'resin',
      url: '/general/dubai_rooftop_shisha_lounge_alt.webp',
      title: 'Resin Coffee Table in Dubai Shisha Lounge'
    },
    {
      id: 19,
      category: 'resin',
      url: '/general/dubai_hotel_lobby_with_geometric_tables.webp',
      title: 'Geometric Resin Tables in Dubai Hotel Lobby'
    },
    {
      id: 20,
      category: 'resin',
      url: '/general/oceanview.webp',
      title: 'Unique Resin Coffee Table with Ocean View'
    },
    {
      id: 21,
      category: 'resin',
      url: '/general/bright_pink_table_in_contemporary_lounge.webp',
      title: 'Exclusive Pink Resin Coffee Table in Contemporary Lounge'
    },
    {
      id: 23,
      category: 'resin',
      url: '/general/bright_pink_table_in_scandinavian_minimalist_living_room.webp',
      title: 'Unique Pink Resin Coffee Table in Scandinavian Interior'
    },
    {
      id: 24,
      category: 'resin',
      url: '/general/modern_minimalist_living_room_with_decorative_table.webp',
      title: 'Handcrafted Resin Coffee Table in Minimalist Living Room'
    },
    {
      id: 25,
      category: 'resin',
      url: '/general/table_light_gray_hot_pink_pattern.webp',
      title: 'Exclusive Resin Coffee Table with Pink Pattern Design'
    }
  ];

  return (
    <div className="min-h-screen bg-black pt-24 pb-12">
      <div className="container mx-auto px-6 max-w-7xl">
        {/* Back Button */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-[hsl(45,100%,70%)] transition-colors group ml-52"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            Back to home
          </Link>
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-[hsl(45,100%,70%)] text-sm font-medium mb-4 tracking-widest uppercase">
            Interior Inspirations
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            <span className="text-[hsl(45,100%,70%)]">Lifestyle</span> Gallery
          </h1>
          <p className="text-gray-300 text-lg max-w-3xl mx-auto leading-relaxed">
            Experience our complete collection beautifully styled in real living spaces. From bird cage lightings to handcrafted sculptures—discover how Artourius transforms interiors into extraordinary environments.
          </p>
        </div>

        {/* Image Gallery Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {images.map((image, index) => (
            <div
              key={image.id}
              className="group relative overflow-hidden rounded-2xl cursor-pointer bg-gray-900"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSelectedImage(image);
                setSelectedIndex(index);
              }}
              style={{
                animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
              }}
            >
              {/* Image */}
              <div className="aspect-square overflow-hidden">
                <img
                  src={image.url}
                  alt={image.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
              </div>

              {/* Coming Soon Overlay for Resin Tables */}
              {image.category === 'resin' && (
                <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                  <img
                    src="/coming-soon.svg"
                    alt="Coming Soon"
                    className="w-2/3 h-auto opacity-90"
                  />
                </div>
              )}

              {/* Overlay - appears only on hover */}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

              {/* Title - appears only on hover */}
              <div className="absolute inset-0 p-6 flex items-end pointer-events-none">
                <div className="transform transition-all duration-500 opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0">
                  <h3 className="text-white text-base font-semibold">
                    {image.title}
                  </h3>
                </div>
              </div>

              {/* Golden border on hover */}
              <div className="absolute inset-0 border-2 border-transparent group-hover:border-[hsl(45,100%,70%)] transition-all duration-500 rounded-2xl pointer-events-none" />
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-24 text-center">
          <div className="bg-gradient-to-r from-gray-900 to-black rounded-2xl p-12 border border-gray-800">
            <h2 className="text-3xl font-bold text-white mb-4">
              Inspired by what you see?
            </h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Bring these stunning designs to your space. Contact us to discuss your custom pieces.
            </p>
            <div className="flex justify-center">
              <a
                href="/contact"
                className="px-8 py-4 bg-[hsl(45,100%,70%)] text-black font-bold rounded-full hover:shadow-[0_0_30px_rgba(255,215,0,0.5)] transition-all duration-300 hover:scale-105"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/95 backdrop-blur-xl flex items-center justify-center p-4"
          style={{ zIndex: 9999 }}
          onClick={() => setSelectedImage(null)}
        >
          {/* Close button */}
          <button
            onClick={(e) => { e.stopPropagation(); setSelectedImage(null); }}
            className="absolute top-8 right-8 w-12 h-12 rounded-full bg-[hsl(45,100%,70%)] flex items-center justify-center hover:scale-110 transition-transform"
            style={{ zIndex: 10000 }}
          >
            <X className="w-6 h-6 text-black" />
          </button>

          {/* Previous Arrow */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              const newIndex = selectedIndex === 0 ? images.length - 1 : selectedIndex - 1;
              setSelectedIndex(newIndex);
              setSelectedImage(images[newIndex]);
            }}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 border-2 border-[hsl(45,100%,70%)] flex items-center justify-center hover:bg-[hsl(45,100%,70%)] hover:scale-110 transition-all group"
            style={{ zIndex: 10000 }}
          >
            <ChevronLeft className="w-6 h-6 text-[hsl(45,100%,70%)] group-hover:text-black" />
          </button>

          {/* Next Arrow */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              const newIndex = selectedIndex === images.length - 1 ? 0 : selectedIndex + 1;
              setSelectedIndex(newIndex);
              setSelectedImage(images[newIndex]);
            }}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 border-2 border-[hsl(45,100%,70%)] flex items-center justify-center hover:bg-[hsl(45,100%,70%)] hover:scale-110 transition-all group"
            style={{ zIndex: 10000 }}
          >
            <ChevronRight className="w-6 h-6 text-[hsl(45,100%,70%)] group-hover:text-black" />
          </button>

          <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedImage.url}
              alt={selectedImage.title}
              className="w-full h-auto max-h-[85vh] object-contain rounded-2xl"
            />
            <div className="text-center mt-6">
              <h3 className="text-xl font-semibold text-white">
                {selectedImage.title}
              </h3>
              <p className="text-sm text-gray-400 mt-2">
                {selectedIndex + 1} / {images.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IdeasImpressions;
